import React, {useEffect, useState} from 'react'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import {
  PaymentMethodSelectorBody,
  PaymentMethodSelectorButtons,
  CheckoutWithCardButton,
  ApplePayButton,
} from '../styles/PaymentMethodSelector.styles'
import {styled} from "@mui/system";
import {ApplePayClient} from '../api/ApplePayClient'
import {DigitalPaymentMethod} from './stepper/DPaymentMethod'

export const PaymentButtonsContent = styled('div')`
  max-width: 600px;
  padding: 0 24px;
  margin: auto;
  box-sizing: border-box;

  ${props => props.theme.breakpoints.up('laptop')} {
    padding: 20px;
  }
`;

type Props = {
  digitalMethods?: DigitalPaymentMethod[]
  onCheckoutWithCard: () => void
  onCheckoutWithAppleApprovedByUser: (payment: any) => Promise<boolean>
  // Google Pay is not offered. The button was removed (see below) but the prop and
  // DPaymentMethod's handleGooglePayCheckout are kept so re-enabling is a UI change
  // only — nothing here consumes it today.
  onCheckoutWithGoogleApprovedByUser: (token: string) => boolean
}

// No Google Pay. The button is removed and GooglePayClient is deliberately not
// imported: probing it pulls in the pay.google.com SDK, whose <script> tag was
// removed from public/index.html because it is blocked by the CSP on every page
// load. Guarded by googlepay-button-removed.test.tsx.
const PaymentMethodSelector: React.FC<Props> = ({digitalMethods, onCheckoutWithCard, onCheckoutWithAppleApprovedByUser}) => {
  const [applePayAvailable, setApplePayAvailable] = useState(false)
  // True while an Apple Pay session is live — disables the button (no second tap) and
  // is reset on every settlement (success or any failure terminal) via the finally below.
  const [applePayInFlight, setApplePayInFlight] = useState(false)

  const applePayMethod = digitalMethods?.find(dm => dm.type === 'applepay')

  useEffect(() => {
    ApplePayClient.canMakePayments().then(setApplePayAvailable)
  }, [])

  const onApplePay = async () => {
    // Duplicate-tap guard: a session is already live — no-op. The button is also disabled
    // during a session (below); this backstops a programmatic or racing second tap.
    if (applePayInFlight) return
    setApplePayInFlight(true)
    try {
      await ApplePayClient.startPayment(
        applePayMethod!.tokenizer,
        { label: applePayMethod!.label, amount: String(applePayMethod!.amount) },
        onCheckoutWithAppleApprovedByUser
      )
    } catch (e) {
      // startPayment rejects with a typed reason on every non-completed terminal
      // (cancelled / timed-out / validation-abort / failed). Cancel is a user choice,
      // not an error — the quiet return to method selection is the intended rescue.
      console.log("Apple Pay error:", e)
    } finally {
      setApplePayInFlight(false)
    }
  }

  return (
    <PaymentButtonsContent>
      <PaymentMethodSelectorBody>
        <PaymentMethodSelectorButtons>
          <CheckoutWithCardButton onClick={onCheckoutWithCard}>
            <CreditCardIcon fontSize="small"/>
            Checkout with card
          </CheckoutWithCardButton>

          {applePayAvailable && applePayMethod && <ApplePayButton onClick={onApplePay} disabled={applePayInFlight}>
            <svg width="20" height="24" viewBox="0 0 20 24" fill="white">
              <path d="M15.07 11.51c-.02-2.25 1.84-3.33 1.92-3.38-1.04-1.53-2.67-1.74-3.25-1.76-1.38-.14-2.7.81-3.4.81-.7 0-1.78-.79-2.93-.77-1.51.02-2.9.88-3.68 2.23-1.57 2.72-.4 6.76 1.13 8.97.75 1.08 1.64 2.3 2.81 2.26 1.13-.05 1.55-.73 2.91-.73 1.36 0 1.75.73 2.94.7 1.21-.02 1.99-1.1 2.73-2.19.86-1.25 1.21-2.47 1.23-2.53-.03-.01-2.36-.91-2.39-3.6zM12.84 4.75c.62-.75 1.04-1.8.93-2.84-.9.04-1.99.6-2.63 1.35-.58.67-1.08 1.73-.95 2.75.99.08 2.01-.51 2.65-1.26z"/>
            </svg>
            Pay
          </ApplePayButton>}
        </PaymentMethodSelectorButtons>
      </PaymentMethodSelectorBody>
    </PaymentButtonsContent>
  )
}

export default PaymentMethodSelector
